import { BundleQuestionFragment, isWeb } from "@stepapp/core";
import type { OrderAttributePayload, ReplacementCheckoutInput } from "@stepapp/core/apiTypes";
import { Block, Loader, Typography } from "@stepapp/ui";
import { useCallback } from "react";
import type { UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SimpleAnswerItem from "../../components/Service/SimpleAnswerItem";

type EditAttributesProps = {
  question?: BundleQuestionFragment;
  attributes?: OrderAttributePayload[];
  questionId?: string;
  setValue: UseFormSetValue<ReplacementCheckoutInput>;
  loading: boolean;
};

const EditAttributes = ({
  question,
  attributes,
  questionId,
  setValue,
  loading,
}: EditAttributesProps) => {
  const { t } = useTranslation();

  const setNewAttribute = useCallback(
    (attributes: (OrderAttributePayload | null)[]) => {
      setValue("attributes", attributes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  if (loading) {
    return (
      <Block alignCenter justifyCenter height={16}>
        <Loader />
      </Block>
    );
  }

  return (
    <Block marginVertical={3} marginHorizontal={2.5}>
      <Typography variant="newH3">
        {question ? question?.name : t("screens.serviceDetails.questionNotFound")}
      </Typography>
      <Block marginTop={1} marginBottom={2}>
        <Typography type="secondary" variant="lightLabel">
          {question ? question?.description : t("screens.serviceDetails.questionCantBeUpdated")}
        </Typography>
      </Block>

      {question?.answers
        .concat()
        .sort((a, b) => a.position - b.position)
        .map(({ id, name, description, isFeatured }) => {
          const isSelected = !!attributes?.find((a) => a?.valueId?.includes(id));

          return (
            <Block key={id} marginBottom={isWeb ? 1 : 0}>
              <SimpleAnswerItem
                description={description}
                id={id}
                isFeatured={isFeatured}
                setNewAttribute={setNewAttribute}
                questionId={questionId || ""}
                questionType={question.type}
                selected={isSelected}
                attributes={attributes}
                title={name}
                numericType={question?.numericType}
              />
            </Block>
          );
        })}
    </Block>
  );
};

export default EditAttributes;
